import * as React from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { inputLabelClasses } from "@mui/material/InputLabel";
import { app, socketApp } from "../../services";
import {
  getDocumentDownloadUrl,
  uploadDocumentFile,
} from "../../services/documents";
import CreditBrokerOnboardingModal from "../../componentsBroker/CreditBrokerOnboardingModal";
import ButtonDocsInput from "../../componentsBroker/ButtonDocsInput";

const SCOPE_OPTIONS = [
  { id: "mine", label: "Asignados a mí" },
  { id: "groups", label: "Por mis grupos" },
  { id: "mine_or_groups", label: "Ambos" },
];

const STATUS_OPTIONS = [
  { id: "", label: "Todos" },
  { id: "nuevo", label: "Nuevo" },
  { id: "asignado", label: "Asignado" },
  { id: "en_proceso", label: "En proceso" },
  { id: "riesgo", label: "Riesgo" },
  { id: "cerrado", label: "Cerrado" },
];

const normalizeRows = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res)) return res;
  return [];
};

const statusColor = (status) => {
  if (status === "cerrado") return "success";
  if (status === "riesgo") return "error";
  if (status === "en_proceso") return "info";
  if (status === "asignado") return "warning";
  return "default";
};

const fullName = (client) =>
  [client?.vc_nombre, client?.vc_paterno, client?.vc_materno]
    .filter(Boolean)
    .join(" ");

const collectProcessFields = (process = {}) => {
  const direct = Array.isArray(process.fields) ? process.fields : [];
  const children = Array.isArray(process.sub_processes)
    ? process.sub_processes
    : [];
  return [
    ...direct,
    ...children.flatMap((child) => collectProcessFields(child)),
  ];
};

const collectSchemaFields = (schema) =>
  (schema?.processes || []).flatMap((process) => collectProcessFields(process));

const processProgress = (process) => {
  const all = collectProcessFields(process).filter((f) => !f?.is_virtual);
  if (!all.length) return 0;
  const done = all.filter((f) => {
    const textValue = f?.value?.vc_valor ?? f?.value?.tx_valor ?? "";
    const hasNumeric =
      f?.value?.nu_valor !== undefined &&
      f?.value?.nu_valor !== null &&
      `${f?.value?.nu_valor}`.trim() !== "";
    const hasDate =
      f?.value?.dt_valor !== undefined &&
      f?.value?.dt_valor !== null &&
      `${f?.value?.dt_valor}`.trim() !== "";
    const hasDoc = !!f?.value?.id_documento;
    return String(textValue).trim() !== "" || hasNumeric || hasDate || hasDoc;
  }).length;
  return Math.round((done / all.length) * 100);
};

const isMoneyType = (typeOrField) => {
  const type = String(
    typeof typeOrField === "string" ? typeOrField : typeOrField?.vc_tipo || "",
  ).toLowerCase();
  return (
    type.includes("moneda") ||
    type.includes("currency") ||
    type.includes("money")
  );
};

const parseMoneyInput = (value) => {
  if (value === null || value === undefined) return "";
  let text = String(value).trim();
  if (!text) return "";
  text = text.replace(/[^0-9,.\-]/g, "");
  if (text.includes(",") && !text.includes(".")) {
    text = text.replace(/,/g, ".");
  }
  text = text.replace(/,/g, "");
  const num = Number(text);
  if (!Number.isFinite(num)) return "";
  return String(num);
};

const formatMoneyMx = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(parseMoneyInput(value));
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const documentStatusLabel = (status) => {
  const value = `${status || ""}`.toLowerCase();
  if (["done", "validado", "recibido"].includes(value)) return "Validado";
  if (["processing", "procesando"].includes(value)) return "Procesando";
  if (["error", "rechazado"].includes(value)) return "Rechazado";
  return "Pendiente";
};

const documentStatusColor = (status) => {
  const value = `${status || ""}`.toLowerCase();
  if (["done", "validado", "recibido"].includes(value)) return "success";
  if (["processing", "procesando"].includes(value)) return "info";
  if (["error", "rechazado"].includes(value)) return "error";
  return "warning";
};

export default function DashboardView() {
  const theme = useTheme();
  const institution = useSelector((state) => state.session.institution);
  const globalUser = useSelector((state) => state.session.globalUser);

  const [scope, setScope] = React.useState("mine_or_groups");
  const [status, setStatus] = React.useState("");
  const [search, setSearch] = React.useState("");

  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");

  const [clients, setClients] = React.useState([]);
  const [kpi, setKpi] = React.useState(null);

  const [selectedClient, setSelectedClient] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [clientUsers, setClientUsers] = React.useState([]);
  const [clientGroups, setClientGroups] = React.useState([]);
  const [productAssignments, setProductAssignments] = React.useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = React.useState("");
  const [dynamicSchema, setDynamicSchema] = React.useState(null);
  const [dynamicLoading, setDynamicLoading] = React.useState(false);
  const [dynamicError, setDynamicError] = React.useState("");
  const [formValues, setFormValues] = React.useState({});
  const [documentFiles, setDocumentFiles] = React.useState({});
  const [documentsById, setDocumentsById] = React.useState({});
  const [savingDynamic, setSavingDynamic] = React.useState(false);
  const [productsCatalog, setProductsCatalog] = React.useState([]);
  const [variantsCatalog, setVariantsCatalog] = React.useState([]);
  const [newAssignmentProductId, setNewAssignmentProductId] =
    React.useState("");
  const [newAssignmentVariantId, setNewAssignmentVariantId] =
    React.useState("");
  const [newAssignmentStatus, setNewAssignmentStatus] =
    React.useState("pendiente");
  const [creatingAssignment, setCreatingAssignment] = React.useState(false);
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [creditReportLoading, setCreditReportLoading] = React.useState(false);
  const [creditReportFeedback, setCreditReportFeedback] = React.useState("");
  const [securityTestLoading, setSecurityTestLoading] = React.useState(false);
  const [securityTestFeedback, setSecurityTestFeedback] = React.useState("");

  const clientsService = React.useMemo(() => app.service("clients"), []);
  const dashboardService = React.useMemo(
    () => app.service("clients-dashboard"),
    [],
  );

  const loadData = React.useCallback(
    async (silent = false) => {
      if (!institution?.id) {
        setLoading(false);
        setRefreshing(false);
        setClients([]);
        setKpi(null);
        setError(
          "Selecciona una empresa/institución para consultar tus clientes.",
        );
        return;
      }

      if (!silent) setLoading(true);
      if (silent) setRefreshing(true);
      setError("");

      try {
        const baseQuery = {
          scope,
          id_institucion: institution?.id,
          $limit: 100,
          $skip: 0,
          $sort: { updatedAt: -1 },
        };

        if (status) baseQuery.vc_estatus = status;
        if (search?.trim()) baseQuery.search = search.trim();

        const [clientsRes, dashboardRes] = await Promise.all([
          clientsService.find({ query: baseQuery }),
          dashboardService.find({
            query: {
              scope,
              id_institucion: institution?.id,
              period: "30d",
              tz: "America/Mazatlan",
            },
          }),
        ]);

        const rows = normalizeRows(clientsRes);
        const dashboardData = normalizeRows(dashboardRes)[0] || null;

        setClients(rows);
        setKpi(dashboardData);
      } catch (e) {
        console.error("Mis clientes load error", e);
        setError(
          "No fue posible cargar Mis clientes con datos reales. Verifica API/sesión y vuelve a intentar.",
        );
        setClients([]);
        setKpi(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [clientsService, dashboardService, institution?.id, scope, status, search],
  );

  const loadClientDetail = React.useCallback(
    async (client) => {
      if (!client?.id || !institution?.id) return;
      setDetailLoading(true);

      try {
        const [usersRes, groupsRes, assignmentsRes] = await Promise.all([
          app.service("client-users").find({
            query: {
              id_cliente: client.id,
              id_institucion: institution?.id,
              $limit: 50,
              $skip: 0,
            },
          }),
          app.service("client-groups").find({
            query: {
              id_cliente: client.id,
              id_institucion: institution?.id,
              $limit: 50,
              $skip: 0,
            },
          }),
          app.service("product-assignments").find({
            query: {
              id_cliente: client.id,
              id_institucion: institution?.id,
              $limit: 50,
              $skip: 0,
              $sort: { updatedAt: -1 },
            },
          }),
        ]);

        setClientUsers(normalizeRows(usersRes));
        setClientGroups(normalizeRows(groupsRes));
        let assignments = normalizeRows(assignmentsRes);

        // Hidrata nombres de producto/variante si backend no expandió include
        const missingProductInfo = assignments.some(
          (a) => !a?.producto && !!a?.id_producto,
        );
        if (missingProductInfo) {
          const productIds = Array.from(
            new Set(assignments.map((a) => a?.id_producto).filter(Boolean)),
          );
          const variantIds = Array.from(
            new Set(assignments.map((a) => a?.id_variante).filter(Boolean)),
          );

          const [productsRes, variantsRes] = await Promise.all([
            productIds.length
              ? app.service("products").find({
                  query: {
                    id_institucion: institution?.id,
                    $limit: 200,
                    $skip: 0,
                    $and: [{ id: { $in: productIds } }],
                  },
                })
              : Promise.resolve({ data: [] }),
            variantIds.length
              ? app.service("product-variants").find({
                  query: {
                    id_institucion: institution?.id,
                    $limit: 200,
                    $skip: 0,
                    $and: [{ id: { $in: variantIds } }],
                  },
                })
              : Promise.resolve({ data: [] }),
          ]);

          const products = normalizeRows(productsRes);
          const variants = normalizeRows(variantsRes);
          const productById = new Map(products.map((p) => [String(p.id), p]));
          const variantById = new Map(variants.map((v) => [String(v.id), v]));

          assignments = assignments.map((a) => ({
            ...a,
            producto:
              a?.producto || productById.get(String(a?.id_producto)) || null,
            variante:
              a?.variante || variantById.get(String(a?.id_variante)) || null,
          }));
        }

        setProductAssignments(assignments);
        const first = assignments[0] || null;
        setSelectedAssignmentId(first?.id || "");
      } catch (e) {
        console.error("loadClientDetail error", e);
        setClientUsers([]);
        setClientGroups([]);
        setProductAssignments([]);
        setSelectedAssignmentId("");
      } finally {
        setDetailLoading(false);
      }
    },
    [institution?.id],
  );

  const loadAssignmentCatalogs = React.useCallback(async () => {
    if (!institution?.id) return;
    try {
      const [productsRes, variantsRes] = await Promise.all([
        app.service("products").find({
          query: {
            id_institucion: institution.id,
            bl_activo: true,
            $limit: 300,
            $skip: 0,
            $sort: { vc_nombre: 1 },
          },
        }),
        app.service("product-variants").find({
          query: {
            id_institucion: institution.id,
            bl_activo: true,
            $limit: 300,
            $skip: 0,
            $sort: { vc_nombre: 1 },
          },
        }),
      ]);

      setProductsCatalog(normalizeRows(productsRes));
      setVariantsCatalog(normalizeRows(variantsRes));
    } catch (e) {
      console.error("loadAssignmentCatalogs error", e);
      setProductsCatalog([]);
      setVariantsCatalog([]);
    }
  }, [institution?.id]);

  const createAssignment = React.useCallback(async () => {
    if (!selectedClient?.id || !institution?.id || !newAssignmentProductId)
      return;
    setCreatingAssignment(true);
    setDynamicError("");
    try {
      await app.service("product-assignments").create({
        id_institucion: institution.id,
        id_cliente: selectedClient.id,
        id_producto: newAssignmentProductId,
        id_variante: newAssignmentVariantId || null,
        vc_estatus: newAssignmentStatus || "pendiente",
      });

      await loadClientDetail(selectedClient);
      setNewAssignmentVariantId("");
      await loadData(true);
    } catch (e) {
      console.error("createAssignment error", e);
      setDynamicError(e?.message || "No fue posible crear la asignación.");
    } finally {
      setCreatingAssignment(false);
    }
  }, [
    selectedClient,
    institution?.id,
    newAssignmentProductId,
    newAssignmentVariantId,
    newAssignmentStatus,
    loadClientDetail,
    loadData,
  ]);

  const loadDynamicSchema = React.useCallback(
    async (client, assignmentId, assignments) => {
      if (!client?.id || !assignmentId || !institution?.id) {
        setDynamicSchema(null);
        return;
      }

      const assignment = (assignments || []).find(
        (a) => String(a.id) === String(assignmentId),
      );
      if (!assignment?.id_producto) {
        setDynamicSchema(null);
        return;
      }

      setDynamicLoading(true);
      setDynamicError("");

      try {
        const response = await app.service("product-form-schema").find({
          query: {
            id_institucion: institution.id,
            id_cliente: client.id,
            id_product_assignment: assignment.id,
            id_producto: assignment.id_producto,
            id_variante: assignment.id_variante || undefined,
          },
        });

        const schema = normalizeRows(response)[0] || null;
        setDynamicSchema(schema);

        const nextValues = {};
        const documentIds = [];
        collectSchemaFields(schema).forEach((field) => {
          const moneyField = isMoneyType(field);
          if (
            field?.value?.vc_valor !== undefined &&
            field?.value?.vc_valor !== null
          ) {
            nextValues[field.id] = field.value.vc_valor;
          } else if (
            field?.value?.nu_valor !== undefined &&
            field?.value?.nu_valor !== null
          ) {
            nextValues[field.id] = moneyField
              ? String(field.value.nu_valor)
              : field.value.nu_valor;
          } else if (
            field?.value?.tx_valor !== undefined &&
            field?.value?.tx_valor !== null
          ) {
            nextValues[field.id] = field.value.tx_valor;
          }
          if (field?.value?.id_documento)
            documentIds.push(field.value.id_documento);
        });
        setFormValues(nextValues);
        setDocumentFiles({});
        if (documentIds.length) {
          const docsRes = await app.service("documents").find({
            query: {
              id_institucion: institution.id,
              id: { $in: Array.from(new Set(documentIds)) },
              $limit: Math.max(100, documentIds.length + 5),
            },
          });
          const map = {};
          normalizeRows(docsRes).forEach((doc) => {
            if (!doc?.id) return;
            map[String(doc.id)] = doc;
          });
          setDocumentsById(map);
        } else {
          setDocumentsById({});
        }
      } catch (e) {
        console.error("loadDynamicSchema error", e);
        setDynamicSchema(null);
        setDynamicError(
          "No fue posible cargar el formulario dinámico para esta asignación.",
        );
      } finally {
        setDynamicLoading(false);
      }
    },
    [institution?.id],
  );

  const saveDynamicValues = React.useCallback(async () => {
    if (
      !selectedClient?.id ||
      !selectedAssignmentId ||
      !institution?.id ||
      !dynamicSchema
    )
      return;

    const assignment = (productAssignments || []).find(
      (a) => String(a.id) === String(selectedAssignmentId),
    );
    if (!assignment) return;

    const fields = collectSchemaFields(dynamicSchema);

    setSavingDynamic(true);
    setDynamicError("");
    try {
      for (const field of fields) {
        if (!field?.id || field?.is_virtual) continue;

        const typedValue = formValues[field.id];
        const file = documentFiles[field.id];

        if (
          (typedValue === undefined ||
            typedValue === null ||
            typedValue === "") &&
          !file
        ) {
          continue;
        }

        const fieldType = String(field?.vc_tipo || "").toLowerCase();
        const fieldIsMoney = isMoneyType(fieldType);
        let idDocumento = null;
        let valorFinal = typedValue ?? null;
        let numericValue = null;

        if (file) {
          const doc = await uploadDocumentFile({
            file,
            id_institucion: institution.id,
            id_owner: selectedClient.id,
            id_cliente: selectedClient.id,
            vc_nombre_original: file.name,
          });
          idDocumento = doc?.id || null;
          if (!valorFinal) valorFinal = file.name;
        }

        const existingRes = await app.service("clientes-data").find({
          query: {
            id_institucion: institution.id,
            id_product_assignment: assignment.id,
            id_field: field.id,
            $limit: 1,
            $skip: 0,
          },
        });

        const existing = normalizeRows(existingRes)[0] || null;
        if (fieldIsMoney) {
          const parsed = Number(parseMoneyInput(typedValue));
          numericValue = Number.isFinite(parsed) ? parsed : null;
          valorFinal = null;
        }
        const payload = {
          id_institucion: institution.id,
          id_cliente: selectedClient.id,
          id_product_assignment: assignment.id,
          id_proceso: field?.id_proceso || null,
          id_field: field.id,
          id_documento: idDocumento || existing?.id_documento || null,
          id_usuario: globalUser?.id || null,
          vc_valor: valorFinal,
          nu_valor: fieldIsMoney ? numericValue : null,
          vc_origen: file ? "manual" : "manual",
        };

        if (existing?.id) {
          await app.service("clientes-data").patch(existing.id, payload);
        } else {
          await app.service("clientes-data").create(payload);
        }
      }

      await loadDynamicSchema(
        selectedClient,
        selectedAssignmentId,
        productAssignments,
      );
      await loadData(true);
    } catch (e) {
      console.error("saveDynamicValues error", e);
      setDynamicError("No fue posible guardar los campos dinámicos.");
    } finally {
      setSavingDynamic(false);
    }
  }, [
    selectedClient,
    selectedAssignmentId,
    institution?.id,
    dynamicSchema,
    productAssignments,
    formValues,
    documentFiles,
    globalUser?.id,
    loadDynamicSchema,
    loadData,
  ]);

  React.useEffect(() => {
    loadData(false);
  }, [loadData]);

  React.useEffect(() => {
    const refreshFromSocket = () => loadData(true);

    const bind = (serviceName) => {
      const svc = socketApp.service(serviceName);
      svc.on("created", refreshFromSocket);
      svc.on("patched", refreshFromSocket);
      svc.on("updated", refreshFromSocket);
      svc.on("removed", refreshFromSocket);
      return () => {
        svc.off("created", refreshFromSocket);
        svc.off("patched", refreshFromSocket);
        svc.off("updated", refreshFromSocket);
        svc.off("removed", refreshFromSocket);
      };
    };

    const unbinders = [
      bind("clients"),
      bind("client-users"),
      bind("client-groups"),
      bind("product-assignments"),
      bind("clientes-data"),
      bind("documents"),
    ];

    return () => {
      unbinders.forEach((fn) => fn());
    };
  }, [loadData]);

  React.useEffect(() => {
    if (selectedClient?.id) {
      socketApp
        .service("suscribe")
        .create({ channel: `leads/${selectedClient.id}` })
        .catch(() => null);
      loadClientDetail(selectedClient);
      loadAssignmentCatalogs();
      return () => {
        socketApp
          .service("suscribe")
          .remove({ channel: `leads/${selectedClient.id}` })
          .catch(() => null);
      };
    }
  }, [selectedClient, loadClientDetail, loadAssignmentCatalogs]);

  React.useEffect(() => {
    setNewAssignmentVariantId("");
  }, [newAssignmentProductId]);

  React.useEffect(() => {
    if (selectedClient?.id && selectedAssignmentId) {
      loadDynamicSchema(
        selectedClient,
        selectedAssignmentId,
        productAssignments,
      );
    } else {
      setDynamicSchema(null);
    }
  }, [
    selectedClient,
    selectedAssignmentId,
    productAssignments,
    loadDynamicSchema,
  ]);

  const totals = kpi?.totales || {};
  const kpiCards = [
    { label: "Total clientes", value: totals.total_clientes || 0 },
    { label: "Pendientes", value: totals.pendientes || 0 },
    { label: "En proceso", value: totals.en_proceso || 0 },
    { label: "Concluidos", value: totals.concluidos || 0 },
    { label: "Riesgo", value: totals.riesgo || 0 },
  ];

  const isCreditCheckProcess = (process) => {
    const name = `${process?.vc_nombre || ""}`.toLowerCase();
    return name.includes("buró") || name.includes("buro");
  };

  const requestCreditReport = React.useCallback(async () => {
    if (!selectedClient?.id || !institution?.id) {
      setCreditReportFeedback(
        "Selecciona cliente e institución para solicitar buró.",
      );
      return;
    }

    setCreditReportLoading(true);
    setCreditReportFeedback("");
    try {
      const response = await app.service("credit-reports").create({
        id_institucion: institution.id,
        id_cliente: selectedClient.id,
        id_product_assignment: selectedAssignmentId || null,
        vc_tipo_consulta: "rcc_ficoscore_pld",
      });

      setCreditReportFeedback(
        response?.vc_resumen || "Consulta de buró enviada correctamente.",
      );
      await loadClientDetail(selectedClient);
      if (selectedAssignmentId) {
        await loadDynamicSchema(
          selectedClient,
          selectedAssignmentId,
          productAssignments,
        );
      }
      await loadData(true);
    } catch (error) {
      console.error("requestCreditReport error", error);
      setCreditReportFeedback(
        error?.message || "No fue posible solicitar información crediticia.",
      );
    } finally {
      setCreditReportLoading(false);
    }
  }, [
    selectedClient,
    institution?.id,
    selectedAssignmentId,
    loadClientDetail,
    loadDynamicSchema,
    productAssignments,
    loadData,
  ]);

  const requestSecurityTest = React.useCallback(async () => {
    if (!institution?.id) {
      setSecurityTestFeedback(
        "Selecciona institución para ejecutar SecurityTest.",
      );
      return;
    }

    setSecurityTestLoading(true);
    setSecurityTestFeedback("");
    try {
      const response = await app.service("credit-reports").create({
        id_institucion: institution.id,
        vc_tipo_consulta: "security_test",
        security_attribute: `tenant-${institution.id}-${Date.now()}`,
      });
      setSecurityTestFeedback(
        response?.vc_resumen || "SecurityTest ejecutado.",
      );
    } catch (error) {
      console.error("requestSecurityTest error", error);
      setSecurityTestFeedback(
        error?.message || "No fue posible ejecutar SecurityTest.",
      );
    } finally {
      setSecurityTestLoading(false);
    }
  }, [institution?.id]);

  const renderDynamicField = (field) => {
    const value = formValues[field.id] ?? "";
    const type = String(field?.vc_tipo || "").toLowerCase();
    const isMoney = isMoneyType(type);
    const isDoc =
      type.includes("document") ||
      type.includes("archivo") ||
      type.includes("imagen") ||
      type.includes("pdf") ||
      type.includes("excel") ||
      type.includes("ppt") ||
      type.includes("file");
    const isSelect =
      !!field?.catalogo?.items?.length ||
      ["catalogo", "catalog", "select"].includes(
        String(field?.vc_tipo || "").toLowerCase(),
      );
    const isDate = ["fecha", "date"].includes(
      String(field?.vc_tipo || "").toLowerCase(),
    );
    const fieldLabel = field.vc_label || field.vc_nombre || "Campo";

    if (isDoc) {
      const documentStatus =
        documentsById[String(field?.value?.id_documento)]?.vc_ai_estatus ||
        field?.value?.documento?.vc_ai_estatus ||
        "pending";
      return (
        <Box
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Stack spacing={1}>
            <Typography sx={{ textAlign: "center" }}>
              {field.vc_label}
            </Typography>
            <Button variant="outlined" component="label" size="small">
              Adjuntar archivo
              <input
                hidden
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setDocumentFiles((prev) => ({ ...prev, [field.id]: file }));
                }}
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {documentFiles[field.id]?.name || value || "Sin archivo"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={documentStatusColor(documentStatus)}
                label={documentStatusLabel(documentStatus)}
              />
              {!!field?.value?.id_documento && (
                <Typography variant="caption" color="text.secondary">
                  Documento recibido
                </Typography>
              )}
            </Stack>
            {!!field?.value?.id_documento && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="success.main">
                  Documento guardado
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    window.open(
                      getDocumentDownloadUrl(field.value.id_documento),
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Ver evidencia
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      );
    }

    if (isSelect) {
      return (
        <FormControl fullWidth size="small" variant="standard" sx={{ mt: 0.4 }}>
          <InputLabel>{fieldLabel}</InputLabel>
          <Select
            label={fieldLabel}
            value={value}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, [field.id]: e.target.value }))
            }
            disableUnderline
            sx={{
              px: 1.2,
              py: 0.8,
              backgroundColor: "rgba(200,200,200,0.1)",
              boxShadow: "0px 0px 2px gray",
              borderRadius: "10px",
            }}
          >
            {(field.catalogo?.items || []).map((item) => (
              <MenuItem key={item.id || item.vc_valor} value={item.vc_valor}>
                {item.vc_label || item.vc_valor}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        variant="standard"
        fullWidth
        size="small"
        type={isDate ? "date" : "text"}
        label={fieldLabel}
        value={isMoney ? formatMoneyMx(value) : value}
        InputLabelProps={{
          shrink: true,
          sx: {
            color: "gray",
            padding: "5px 10px",
            [`&.${inputLabelClasses.shrink}`]: {
              padding: "0px 14px",
            },
          },
        }}
        InputProps={{
          placeholder: fieldLabel,
          disableUnderline: true,
          style: {
            padding: "8px 10px",
            backgroundColor: "rgba(200,200,200,0.1)",
            boxShadow: "0px 0px 2px gray",
            borderRadius: 10,
          },
        }}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            [field.id]: isMoney
              ? parseMoneyInput(e.target.value)
              : e.target.value,
          }))
        }
        inputProps={{ inputMode: isMoney ? "decimal" : undefined }}
        sx={{ mt: 0.4 }}
      />
    );
  };

  const renderProcessNode = (process, depth = 0) => {
    const blockRadius = Math.max(
      10,
      Math.round((theme?.shape?.borderRadius || 12) * 1.1),
    );
    return (
      <Box
        key={process.id}
        sx={{
          p: 0,
          ml: depth ? 0 : 0,
          mt: depth ? 1 : 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {process.vc_nombre}
          </Typography>
          <Chip
            size="small"
            color="primary"
            label={`${processProgress(process)}%`}
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={processProgress(process)}
          sx={{ mb: 1.25 }}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box />
          {isCreditCheckProcess(process) && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={requestCreditReport}
                disabled={creditReportLoading}
              >
                {creditReportLoading ? "Consultando..." : "Solicitar buró"}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={requestSecurityTest}
                disabled={securityTestLoading}
              >
                {securityTestLoading ? "Probando..." : "SecurityTest"}
              </Button>
            </Stack>
          )}
        </Stack>
        {!!creditReportFeedback && isCreditCheckProcess(process) && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {creditReportFeedback}
          </Alert>
        )}
        {!!securityTestFeedback && isCreditCheckProcess(process) && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {securityTestFeedback}
          </Alert>
        )}

        <Grid container spacing={1.5}>
          {(process.fields || []).map((field) => (
            <Grid item xs={12} md={6} key={field.id}>
              <Box
                sx={{
                  p: 0,
                }}
              >
                {/* <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.4 }}
                >
                  {field.vc_label || field.vc_nombre}
                </Typography> */}
                {renderDynamicField(field)}
              </Box>
            </Grid>
          ))}
        </Grid>

        {(process.sub_processes || []).map((child) =>
          renderProcessNode(child, depth + 1),
        )}
      </Box>
    );
  };

  return (
    <Box p={2}>
      <Helmet>
        <title>Mis Clientes</title>
        <meta
          name="description"
          content="Mis clientes conectados a datos reales"
        />
      </Helmet>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={800}>
          Mis clientes
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {refreshing && <CircularProgress size={18} />}
          <Button variant="outlined" onClick={() => setWizardOpen(true)}>
            Nuevo cliente
          </Button>
          <Button variant="contained" onClick={() => loadData(false)}>
            Actualizar
          </Button>
        </Stack>
      </Stack>

      {!!error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => loadData(false)}
            >
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        {kpiCards.map((item) => (
          <Grid item xs={12} sm={6} md={2.4} key={item.label}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="scope-label">Alcance</InputLabel>
            <Select
              labelId="scope-label"
              value={scope}
              label="Alcance"
              onChange={(e) => setScope(e.target.value)}
            >
              {SCOPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-label">Estatus</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              label="Estatus"
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.id || "all"} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Buscar por nombre, correo o teléfono"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
      </Grid>

      <TableContainer
        sx={{ borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Contacto</TableCell>
              <TableCell>Estatus</TableCell>
              <TableCell>Producto activo</TableCell>
              <TableCell>Avance expediente</TableCell>
              <TableCell>Última actualización</TableCell>
              <TableCell align="right">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      Cargando clientes...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : clients.length ? (
              clients.map((client) => (
                <TableRow key={client.id} hover>
                  <TableCell>
                    <Stack spacing={0.4}>
                      <Typography fontWeight={700}>
                        {fullName(client) || client?.vc_nombre || "Sin nombre"}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {!!client?.asignado_directo && (
                          <Chip size="small" color="primary" label="Directo" />
                        )}
                        {!!client?.asignado_por_grupo && (
                          <Chip size="small" color="secondary" label="Grupo" />
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {client?.vc_correo || "—"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {client?.vc_telefono || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={client?.vc_estatus || "—"}
                      color={statusColor(client?.vc_estatus)}
                    />
                  </TableCell>
                  <TableCell>{client?.producto_activo || "—"}</TableCell>
                  <TableCell>
                    {Number(client?.avance_expediente || 0)}%
                  </TableCell>
                  <TableCell>
                    {client?.updatedAt
                      ? new Date(client.updatedAt).toLocaleString("es-MX")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedClient(client)}
                    >
                      Detalle
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  No hay clientes para el filtro seleccionado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle cliente</DialogTitle>
        <DialogContent>
          {!selectedClient ? null : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  {fullName(selectedClient) || "Cliente"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedClient?.vc_correo || "Sin correo"} ·{" "}
                  {selectedClient?.vc_telefono || "Sin teléfono"}
                </Typography>
              </Box>

              {detailLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Cargando detalle...</Typography>
                </Stack>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" mb={1}>
                      Usuarios asignados
                    </Typography>
                    <Stack spacing={0.5}>
                      {clientUsers.length ? (
                        clientUsers.map((item) => (
                          <Chip
                            key={item.id}
                            size="small"
                            label={item?.id_usuario || "usuario"}
                          />
                        ))
                      ) : (
                        <Typography variant="body2">
                          Sin usuarios asignados
                        </Typography>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" mb={1}>
                      Grupos asignados
                    </Typography>
                    <Stack spacing={0.5}>
                      {clientGroups.length ? (
                        clientGroups.map((item) => (
                          <Chip
                            key={item.id}
                            size="small"
                            label={item?.id_grupo || "grupo"}
                          />
                        ))
                      ) : (
                        <Typography variant="body2">
                          Sin grupos asignados
                        </Typography>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" mb={1}>
                      Productos asignados
                    </Typography>
                    <Stack spacing={0.5}>
                      {productAssignments.length ? (
                        productAssignments.map((item) => (
                          <Chip
                            key={item.id}
                            size="small"
                            label={`${item?.producto?.vc_nombre || item?.id_producto || "producto"}${item?.variante?.vc_nombre ? ` / ${item.variante.vc_nombre}` : ""} · ${item?.vc_estatus || "pendiente"}`}
                          />
                        ))
                      ) : (
                        <Typography variant="body2">
                          Sin productos asignados
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )}

              <Box>
                {/* <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Nueva asignación producto/variante
                </Typography>
                <Grid container spacing={1.5} mb={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="new-product-label">Producto</InputLabel>
                      <Select
                        labelId="new-product-label"
                        label="Producto"
                        value={newAssignmentProductId}
                        onChange={(e) =>
                          setNewAssignmentProductId(e.target.value)
                        }
                      >
                        {productsCatalog.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.vc_nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="new-variant-label">Variante</InputLabel>
                      <Select
                        labelId="new-variant-label"
                        label="Variante"
                        value={newAssignmentVariantId}
                        onChange={(e) =>
                          setNewAssignmentVariantId(e.target.value)
                        }
                      >
                        <MenuItem value="">Sin variante</MenuItem>
                        {variantsCatalog
                          .filter(
                            (v) =>
                              !newAssignmentProductId ||
                              String(v.id_producto) ===
                                String(newAssignmentProductId),
                          )
                          .map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                              {v.vc_nombre}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="new-status-label">Estatus</InputLabel>
                      <Select
                        labelId="new-status-label"
                        label="Estatus"
                        value={newAssignmentStatus}
                        onChange={(e) => setNewAssignmentStatus(e.target.value)}
                      >
                        <MenuItem value="pendiente">Pendiente</MenuItem>
                        <MenuItem value="en_proceso">En proceso</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      disabled={!newAssignmentProductId || creatingAssignment}
                      onClick={createAssignment}
                    >
                      Asignar
                    </Button>
                  </Grid>
                </Grid> */}

                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Captura dinámica por proceso
                </Typography>
                <Grid container spacing={2} mb={1}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="assignment-label">
                        Asignación de producto
                      </InputLabel>
                      <Select
                        labelId="assignment-label"
                        label="Asignación de producto"
                        value={selectedAssignmentId}
                        onChange={(e) =>
                          setSelectedAssignmentId(e.target.value)
                        }
                      >
                        {(productAssignments || []).map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item?.producto?.vc_nombre ||
                              item?.id_producto ||
                              "producto"}
                            {item?.variante?.vc_nombre
                              ? ` / ${item.variante.vc_nombre}`
                              : ""}
                            {" · "}
                            {item?.vc_estatus || "pendiente"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {!!dynamicError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {dynamicError}
                  </Alert>
                )}

                {dynamicLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">
                      Cargando esquema dinámico...
                    </Typography>
                  </Stack>
                ) : dynamicSchema ? (
                  <Stack spacing={2}>
                    {(dynamicSchema.processes || []).map((process) =>
                      renderProcessNode(process),
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Selecciona una asignación para cargar el formulario
                    dinámico.
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {/* <Button
            variant="outlined"
            fullWidth
            onClick={() =>
              loadDynamicSchema(
                selectedClient,
                selectedAssignmentId,
                productAssignments,
              )
            }
            disabled={!selectedAssignmentId || dynamicLoading}
          >
            Recargar
          </Button> */}
          <Button
            variant="contained"
            onClick={saveDynamicValues}
            disabled={!selectedAssignmentId || !dynamicSchema || savingDynamic}
          >
            Guardar
          </Button>
          <Button onClick={() => setSelectedClient(null)}>{"Cerrar"}</Button>
        </DialogActions>
      </Dialog>

      <CreditBrokerOnboardingModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={() => {
          setWizardOpen(false);
          loadData(false);
        }}
      />
    </Box>
  );
}
